import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Icon } from '@fluentui/react';
import fetchPendingAssignedInvoiceCount, {
  BILLIT_PENDING_ASSIGNED_REFRESH_EVENT,
} from '../../modules/billit-invoices-assigned/pendingAssignedInvoices';

interface IBillitAssignedInvoicesHeaderNotice {
  /** opmaak van een header-onderdeel (gewone witte tekst, volle hoogte) */
  className?: string;
  /** scheidingslijntje rechts van de melding */
  separatorClassName?: string;
  /** klein stipje op het belletje */
  dotClassName?: string;
}

const BillitAssignedInvoicesHeaderNotice = ({
  className,
  separatorClassName,
  dotClassName,
}: IBillitAssignedInvoicesHeaderNotice) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [pendingCount, setPendingCount] = useState(0);
  const pendingCountRef = useRef(0);

  const loadCount = useCallback(async () => {
    if (!isAuthenticated) {
      if (pendingCountRef.current !== 0) {
        pendingCountRef.current = 0;
        setPendingCount(0);
      }
      return;
    }

    try {
      const count = await fetchPendingAssignedInvoiceCount(getAccessTokenSilently);
      if (pendingCountRef.current !== count) {
        pendingCountRef.current = count;
        setPendingCount(count);
      }
    } catch {
      if (pendingCountRef.current !== 0) {
        pendingCountRef.current = 0;
        setPendingCount(0);
      }
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    loadCount();

    const onRefresh = () => {
      loadCount();
    };

    window.addEventListener(BILLIT_PENDING_ASSIGNED_REFRESH_EVENT, onRefresh);

    return () => {
      window.removeEventListener(BILLIT_PENDING_ASSIGNED_REFRESH_EVENT, onRefresh);
    };
  }, [loadCount]);

  if (!isAuthenticated || pendingCount <= 0) {
    return null;
  }

  const invoiceLabel = pendingCount === 1 ? 'factuur' : 'facturen';

  return (
    <>
      <a
        href='/billit-invoices-assigned'
        className={className}
        title={`${pendingCount} toegewezen ${invoiceLabel} wachten op beoordeling`}
      >
        <span style={{ position: 'relative', display: 'inline-flex', marginRight: 4 }}>
          <Icon iconName='Ringer' />
          {dotClassName && <span className={dotClassName} />}
        </span>
        {`${pendingCount} ${invoiceLabel} te beoordelen`}
      </a>
      {separatorClassName && <span className={separatorClassName} />}
    </>
  );
};

export default memo(BillitAssignedInvoicesHeaderNotice);
